import { X } from 'lucide-react'
import * as React from 'react'

import doubleTireTread from '@/assets/marketing/images/double-tire-tread.svg'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface EstimateBannerProps {
  /** Controls the slide-in/slide-out animation. Ignored when `inline` is set. */
  open?: boolean
  /** Omit to render without a heading line (e.g. the permanent footer copy). */
  title?: string
  description: React.ReactNode
  inputPlaceholder?: string
  buttonLabel: string
  /** Shorter label shown below the `md` breakpoint. Omit to reuse `buttonLabel` on mobile too. */
  buttonLabelMobile?: string
  onSubmit?: (value: string) => void
  /** Omit (or set `inline`) to render with no dismiss button. */
  onDismiss?: () => void
  /**
   * Renders as a plain static block instead of the fixed slide-up overlay
   * — no dismiss button, no card background/shadow, meant to sit inside
   * an already-styled surface like the footer.
   */
  inline?: boolean
  className?: string
}

/**
 * Fixed bottom bar that slides up over page content. Stays mounted at all
 * times so the slide-out transition can play on dismiss — visibility is
 * driven entirely by `open`, not by conditional rendering. Pass `inline`
 * to render the same title/form as a static block instead (e.g. a
 * permanent copy embedded in the footer).
 */
export function EstimateBanner({
  open = false,
  title,
  description,
  inputPlaceholder = 'USDOT 1234567',
  buttonLabel,
  buttonLabelMobile,
  onSubmit,
  onDismiss,
  inline = false,
  className,
}: EstimateBannerProps) {
  const [value, setValue] = React.useState('')

  return (
    <div
      className={cn(
        inline
          ? 'w-full'
          : 'pointer-events-none fixed inset-x-0 bottom-6 z-200 flex translate-y-[120%] justify-center px-5.5 opacity-0 transition-[transform,opacity] duration-[400ms] ease-in-out',
        !inline && open && 'pointer-events-auto translate-y-0 opacity-100',
        className
      )}
      role="region"
      aria-label={title ?? (typeof description === 'string' ? description : buttonLabel)}
      aria-hidden={inline ? undefined : !open}
    >
      <div
        className={cn(
          'bg-ink-900 relative flex w-full max-w-(--maxw) items-center gap-10 overflow-hidden rounded-xl px-8 py-6 text-white shadow-[0_12px_32px_rgba(0,0,0,0.25)] max-md:flex-wrap',
          inline && 'max-w-none rounded-none border-t border-white/12 bg-transparent px-0 py-8 shadow-none'
        )}
      >
        {/* Tire-tread texture across the dark surface. */}
        <div
          aria-hidden="true"
          className="bg-ink-800 pointer-events-none absolute inset-0 mask-size-[auto_140%] mask-left mask-no-repeat"
          style={{ maskImage: `url(${doubleTireTread})`, WebkitMaskImage: `url(${doubleTireTread})` }}
        />
        <div className={cn('relative z-1 max-w-120', !inline && 'max-md:pr-12')}>
          {title ? <p className="mb-2 text-2xl font-bold tracking-[-0.01em]">{title}</p> : null}
          <p className="text-md leading-[1.55] text-white/70">{description}</p>
        </div>
        <form
          className="relative z-1 ml-auto flex flex-none items-center justify-end gap-4 max-md:w-full max-md:gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit?.(value)
          }}
        >
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={8}
            size="lg"
            className="w-auto min-w-[180px] border-white/30 bg-white/10 text-white placeholder:text-white/60 max-md:min-w-[100px] max-md:flex-1"
            placeholder={inputPlaceholder}
            aria-label={inputPlaceholder}
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 8))}
          />
          <Button size="lg" type="submit" className="max-md:px-3">
            {buttonLabelMobile ? (
              <>
                <span className="max-md:hidden">{buttonLabel}</span>
                <span className="hidden max-md:inline">{buttonLabelMobile}</span>
              </>
            ) : (
              buttonLabel
            )}
          </Button>
        </form>
        {!inline && onDismiss ? (
          <Button
            type="button"
            variant="iconOnly"
            size="icon-sm"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="relative z-1 text-white opacity-60 hover:bg-white/10 hover:opacity-100 max-md:absolute max-md:top-4 max-md:right-4"
          >
            <X className="size-5" />
          </Button>
        ) : null}
      </div>
    </div>
  )
}
